import { cn } from "@/lib/utils";
import { useGameStore } from "@/store/gameStore";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { CourtFace } from "./courtFace";
import { Face, isPanel } from "./face";
import { useShareGame } from "./menuComponents";
import { MenuFace } from "./menuFace";
import { SetupFace } from "./setupFace";
import { ShareFace } from "./shareFace";

const colors = "background-color 500ms, color 500ms, box-shadow 500ms";
// The iOS card's bouncy spring between faces.
const spring = `height 680ms var(--ease-bouncy), ${colors}`;

interface GameCardProps {
  face: Face;
  setFace: (face: Face) => void;
}

/**
 * The glass card at the bottom of the play screen. It shows the court to
 * head to, and stretches into the game menu, then court setup or sharing,
 * so each one opens out of the one before it.
 */
export const GameCard: React.FC<GameCardProps> = ({ face, setFace }) => {
  const slug = useGameStore((state) => state.game.slug);
  const share = useShareGame(slug);
  const shown = useShownFaces(face);
  const close = useCallback(() => setFace("court"), [setFace]);
  const card = useRef<HTMLDivElement>(null);
  const faceChangedAt = useRef(0);

  useLayoutEffect(() => {
    faceChangedAt.current = performance.now();
  }, [face]);

  // The card takes the height of the face it shows: it springs there when
  // the face changes, and otherwise follows the face's own animations, like
  // a setup row opening, frame by frame. Set directly, since it changes
  // every frame while a row opens.
  const measure = useCallback((element: HTMLDivElement | null) => {
    if (!element) return;
    const follow = () => {
      if (!card.current) return;
      const changingFace = performance.now() - faceChangedAt.current < 700;
      card.current.style.transition = changingFace ? spring : colors;
      card.current.style.height = `${element.offsetHeight}px`;
    };
    follow();
    const observer = new ResizeObserver(follow);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Escape closes the card, like tapping outside it.
  useEffect(() => {
    if (face === "court") return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [face, close]);

  const showShare = () => {
    setFace("share");
    if (!slug && !share.isLoading) share.handleEnableSync();
  };

  const closeShare = () => {
    close();
    share.resetShareState();
  };

  const render = (shownFace: Face) => {
    switch (shownFace) {
      case "court":
        return <CourtFace onClick={() => setFace("menu")} />;
      case "menu":
        return (
          <MenuFace
            onShare={showShare}
            onSetup={() => setFace("setup")}
            close={close}
          />
        );
      case "setup":
        return <SetupFace close={close} />;
      case "share":
        return <ShareFace share={share} close={closeShare} />;
    }
  };

  return (
    <div
      ref={card}
      className={cn(
        "relative overflow-hidden rounded-[32px] motion-reduce:transition-none!",
        // Court and menu are frosted white in the suit's color, like a
        // card; setup and sharing have their own darker look.
        isPanel(face) ? "glass-panel text-white" : "glass-frost text-suit",
      )}
    >
      {shown.map((shownFace) => {
        const current = shownFace === face;
        return (
          // Pinned to the bottom, so the card grows upward from the
          // controls. Scrolls when it's taller than the screen.
          <div
            key={shownFace}
            ref={current ? measure : undefined}
            className={cn(
              "absolute inset-x-0 bottom-0 max-h-[calc(100dvh-env(safe-area-inset-top)-2rem)] overflow-y-auto overscroll-contain",
              current
                ? "animate-face-in"
                : "animate-face-out pointer-events-none",
            )}
            inert={!current}
          >
            {render(shownFace)}
          </div>
        );
      })}
    </div>
  );
};

/** The face on show, plus the one before it while it fades out. */
const useShownFaces = (face: Face) => {
  const [shown, setShown] = useState<Face[]>([face]);

  useLayoutEffect(() => {
    setShown((previous) => [...previous.filter((f) => f !== face), face]);
    const timer = setTimeout(() => setShown([face]), 250);
    return () => clearTimeout(timer);
  }, [face]);

  return shown;
};
